import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactModal from '../components/ContactModal';
import './AboutUs.css';

gsap.registerPlugin(ScrollTrigger);

const AboutUs = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const statsRef = useRef(null);
    const statItemsRef = useRef([]);

    useEffect(() => {
        const section = statsRef.current;
        const statItems = statItemsRef.current;

        if (!section || statItems.length === 0) return;

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 85%',
                end: 'bottom 20%',
                once: true,
            },
        });

        statItems.forEach((item, index) => {
            if (!item) return;

            const numberEl = item.querySelector('h3');
            if (!numberEl) return;

            const targetText = numberEl.textContent;
            const targetNumber = parseInt(targetText.replace(/[^0-9]/g, ''));
            const counter = { value: 0 };

            // Animate number with scale 0 to 1 and count up
            timeline.fromTo(
                numberEl,
                {
                    scale: 0
                },
                {
                    scale: 1,
                    duration: 0.6,
                    ease: 'back.out(1.7)', // Creates overshoot/bounce effect
                },
                index * 0.2 // Stagger delay between numbers
            );

            // Animate number counting up
            timeline.to(
                counter,
                {
                    value: targetNumber,
                    duration: 1.5,
                    ease: 'power2.out',
                    onUpdate: () => {
                        if (targetText.includes('%')) {
                            numberEl.textContent = `${Math.round(counter.value)}%`;
                        } else {
                            numberEl.textContent = `${Math.round(counter.value)}+`;
                        }
                    },
                },
                index * 0.2 + 0.3 // Start counting shortly after scale animation
            );
        });

    }, []);

    return (
        <div className="page-container">
            {/* Hero Section */}
            <section className="page-hero-section about-hero">
                <div className="page-hero-content">
                    <p className="page-hero-subtitle">ABOUT US</p>
                    <h1 className="page-hero-title">
                        Your One-Stop Resource<br />
                        In Your Chess Journey
                    </h1>
                    <p className="page-hero-description">
                        We're dedicated to growing the Kenyan chess community through expert coaching, curated events, and access to high-quality chess products. Whether you're a beginner or a seasoned player, we're here to support every step of your chess journey.
                    </p>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section" ref={statsRef}>
                <div className="container">
                    <div className="stat-item" ref={(el) => (statItemsRef.current[0] = el)}>
                        <h3>500+</h3>
                        <p>Clients Trained</p>
                    </div>
                    <div className="stat-item" ref={(el) => (statItemsRef.current[1] = el)}>
                        <h3>15+</h3>
                        <p>Schools Trained</p>
                    </div>
                    <div className="stat-item" ref={(el) => (statItemsRef.current[2] = el)}>
                        <h3>98%</h3>
                        <p>Satisfaction Rate</p>
                    </div>
                </div>
            </section>

            {/* Coaching Staff Section */}
            <section className="coaching-staff-section">
                <div className="container">
                    <h2>Expert Coaches, Proven Results</h2>
                    <p className="section-intro">
                        Our coaching staff brings years of competitive experience and a passion for teaching. We train players in essential chess concepts—openings, mid-game strategy, and endgame techniques—while empowering them to learn from the greats who came before. Every session is designed to elevate your game.
                    </p>

                    <div className="coaching-cards-grid">
                        <div className="coaching-card">
                            <div className="coaching-icon">🏆</div>
                            <h3>Championship Experience</h3>
                            <p>
                                Our coaches have competed and won in both local and international chess tournaments. They bring real-world competitive experience to every training session, teaching you the strategies that win games.
                            </p>
                        </div>

                        <div className="coaching-card">
                            <div className="coaching-icon">🤝</div>
                            <h3>Community-Focused Approach</h3>
                            <p>
                                We celebrate your wins and learn from your losses together. Our coaches are deeply invested in building a supportive chess community where every player feels valued and encouraged to grow.
                            </p>
                        </div>

                        <div className="coaching-card">
                            <div className="coaching-icon">🎯</div>
                            <h3>Personalized Training Methods</h3>
                            <p>
                                Every student learns differently. Our coaches curate training methodologies tailored to your learning style—from tactical puzzles to challenging you to fail forward. This personalized approach ensures a high success rate for all our clients.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <button
                            className="btn-cta-primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Book a Coaching Session
                        </button>
                    </div>
                </div>
            </section>

            {/* Events Section */}
            <section className="events-section">
                <div className="container">
                    <h2>Growing the Community, One Event at a Time</h2>
                    <p className="section-intro">
                        Our events are more than just competitions—they're opportunities to connect, challenge yourself, and witness the growth of Kenya's chess scene firsthand.
                    </p>

                    <div className="events-cards-grid">
                        <div className="event-card">
                            <div className="event-icon">👥</div>
                            <h3>Network & Build Friendships</h3>
                            <p>
                                Chess brings people together. Our events create spaces for players to network, bond, and form long-lasting friendships around this challenging and beautiful game. The connections you make here extend far beyond the board.
                            </p>
                        </div>

                        <div className="event-card">
                            <div className="event-icon">⚔️</div>
                            <h3>Challenge Yourself</h3>
                            <p>
                                Test your skills against the competition and gauge your progress. We love seeing clients go from frustration after a tough loss to triumph over opponents who once seemed unbeatable. Every tournament is a step forward.
                            </p>
                        </div>

                        <div className="event-card">
                            <div className="event-icon">🌍</div>
                            <h3>Witness Kenya's Chess Growth</h3>
                            <p>
                                We're proud of the growth we've seen in the Kenyan chess scene since we started. Some of our clients have gone on to excel globally, becoming formidable opponents even to our coaches. This is just the beginning.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <Link to="/events" className="btn-cta-primary">
                            View Upcoming Events
                        </Link>
                    </div>
                </div>
            </section>

            {/* Content Curation Section */}
            <section className="content-section">
                <div className="container">
                    <h2>Curated Chess Content for the Community</h2>
                    <p className="section-intro">
                        Stay connected with the chess community through our carefully crafted content. From event coverage to game analysis, we keep you informed and inspired.
                    </p>

                    <div className="content-cards-grid">
                        <div className="content-card">
                            <div className="content-icon">📰</div>
                            <h3>Event Coverage & Previews</h3>
                            <p>
                                We break down recent and upcoming events—from highlighting the winners to previewing what to expect in the next stage. Stay informed about the chess scene in Kenya and beyond.
                            </p>
                        </div>

                        <div className="content-card">
                            <div className="content-icon">♟️</div>
                            <h3>Game Breakdowns</h3>
                            <p>
                                Learn from the best. We analyze recent wins and lost opportunities from tournament games, helping you understand the critical moments that decide matches and how to capitalize on them.
                            </p>
                        </div>

                        <div className="content-card">
                            <div className="content-icon">📚</div>
                            <h3>Learning Resources</h3>
                            <p>
                                Grow your chess arsenal with our educational content. From featuring unconventional opening moves to trusted endgame techniques, we provide the knowledge you need to level up your game.
                            </p>
                        </div>
                    </div>

                    <div className="section-cta">
                        <Link to="/blog" className="btn-cta-primary">
                            Read Our Blog
                        </Link>
                    </div>
                </div>
            </section>

            {/* Shop Section */}
            <section className="shop-section">
                <div className="container">
                    <h2>Quality Chess Products for Every Player</h2>
                    <p className="section-intro">
                        From chess boards to clocks, we offer high-quality products that empower your chess journey. We strive to ensure every player has access to the equipment they need to practice and compete at their best.
                    </p>
                    <div className="section-cta">
                        <Link to="/shop" className="btn-cta-primary">
                            Browse Our Shop
                        </Link>
                    </div>
                </div>
            </section>

            {/* Location Section */}
            <section className="location-section">
                <div className="container">
                    <h2>Serving Nairobi & Beyond</h2>
                    <p className="location-text">
                        We're based in Nairobi and well-equipped to serve players within the city and its environs. Whether you're looking for in-person coaching or prefer remote sessions, we're here to support chess enthusiasts across Kenya and beyond.
                    </p>
                    <div className="location-details">
                        <div className="location-item">
                            <span className="location-icon">📍</span>
                            <strong>Location:</strong> Nairobi, Kenya
                        </div>
                        <div className="location-item">
                            <span className="location-icon">🎓</span>
                            <strong>In-Person Training:</strong> Nairobi & Environs
                        </div>
                        <div className="location-item">
                            <span className="location-icon">💻</span>
                            <strong>Remote Coaching:</strong> Available Nationwide
                        </div>
                    </div>
                </div>
            </section>

            <ContactModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

export default AboutUs;
